import { InquiryFieldDefinition } from '../_types/inquiry-field'

/**
 * The curated catalogue of optional inquiry fields a host site can switch on
 * via the iframe URL (`?fields=budget,parking`). Keys + labels mirror the
 * server catalogue in `quickpass/inquiry-fields/catalogue.ts`; this copy adds
 * the render details (input type, options, placeholder). Because the host can
 * only reference keys that exist here, it can never inject its own fields.
 */
const INQUIRY_FIELD_CATALOGUE: InquiryFieldDefinition[] = [
  {
    key: 'budget',
    label: 'Monthly budget',
    inputType: 'number',
    placeholder: '2000',
  },
  {
    key: 'moveInDate',
    label: 'Move-in date',
    inputType: 'date',
  },
  {
    key: 'bedrooms',
    label: 'Bedrooms needed',
    inputType: 'select',
    options: [
      { value: 'Studio', label: 'Studio' },
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4+', label: '4+' },
    ],
  },
  {
    key: 'occupants',
    label: 'Number of occupants',
    inputType: 'number',
    placeholder: '2',
  },
  {
    key: 'parking',
    label: 'Parking',
    inputType: 'select',
    options: [
      { value: 'None needed', label: 'None needed' },
      { value: '1 spot', label: '1 spot' },
      { value: '2 spots', label: '2 spots' },
      { value: 'EV charging', label: 'EV charging' },
    ],
  },
  {
    key: 'pets',
    label: 'Pets',
    inputType: 'select',
    options: [
      { value: 'No pets', label: 'No pets' },
      { value: 'Cat', label: 'Cat' },
      { value: 'Dog', label: 'Dog' },
      { value: 'Cat & dog', label: 'Cat & dog' },
      { value: 'Other', label: 'Other' },
    ],
  },
  {
    key: 'income',
    label: 'Monthly income',
    inputType: 'number',
    placeholder: '6000',
  },
  {
    key: 'employment',
    label: 'Employment status',
    inputType: 'select',
    options: [
      { value: 'Employed full-time', label: 'Employed full-time' },
      { value: 'Employed part-time', label: 'Employed part-time' },
      { value: 'Self-employed', label: 'Self-employed' },
      { value: 'Student', label: 'Student' },
      { value: 'Retired', label: 'Retired' },
      { value: 'Other', label: 'Other' },
    ],
  },
  {
    key: 'leaseTerm',
    label: 'Lease length',
    inputType: 'select',
    options: [
      { value: 'Month-to-month', label: 'Month-to-month' },
      { value: '6 months', label: '6 months' },
      { value: '12 months', label: '12 months' },
      { value: 'Flexible', label: 'Flexible' },
    ],
  },
  {
    key: 'preferredContact',
    label: 'Best way to reach you',
    inputType: 'select',
    options: [
      { value: 'Email', label: 'Email' },
      { value: 'Phone call', label: 'Phone call' },
      { value: 'Text', label: 'Text' },
    ],
  },
]

/**
 * Looks up a catalogue field definition by key. Returns null for unknown keys
 * so host-supplied keys outside the catalogue are simply ignored.
 *
 * @param {string} key - the candidate field key from the URL
 * @returns {InquiryFieldDefinition | null}
 */
const getInquiryFieldDefinition = (
  key: string,
): InquiryFieldDefinition | null => {
  return (
    INQUIRY_FIELD_CATALOGUE.find(definition => definition.key === key) ?? null
  )
}

export { INQUIRY_FIELD_CATALOGUE, getInquiryFieldDefinition }
